// lib/feed/feed-active-tab-persistence.ts
//
// Pure domain for persisting the feed page's ACTIVE TAB across navigation
// (sessionStorage + TTL + tab validation). Mirrors the pattern in
// `lib/feed/feed-snapshot.ts` (hex: functional core — no DOM/window access;
// the adapter, `components/feed/feed-page-client-state.ts`, does the
// sessionStorage I/O in a `useState` lazy initializer + a persist effect and
// calls into these pure functions).
//
// WHY: without this, back-nav from a listing detail always re-mounts the feed
// page on the default tab ("live"), even if the user had switched to
// "discover"/"following". A narrow one-off hack (`RESTORE_DISCOVER_TAB_KEY`)
// previously covered ONE navigation path (discover-category drill-down back
// button); this generalizes to every tab switch.
//
// @see components/feed/feed-page-client-state.ts (adapter)
// @see lib/feed-tab-config.ts (coerceFeedActiveTab / isFeedTabVisible — tab
//   validity + login gate, single source of truth for "is this tab legal")
// @see .tondocs/skills/hex.md §1 (functional core, imperative shell)

import type { FeedActiveTab } from "@/components/feed/feed-page-client-state";
import { coerceFeedActiveTab, isFeedTabVisible } from "@/lib/feed-tab-config";

/** sessionStorage key. Bump the version suffix if the persisted shape changes. */
export const FEED_ACTIVE_TAB_STORAGE_KEY = "kraahl_feed_active_tab";

/**
 * ~10 min TTL. Long enough that the common listing->back round-trip (seconds
 * to low minutes) restores the tab; short enough that a tab choice from a much
 * older, abandoned session doesn't stick around indefinitely.
 */
export const FEED_ACTIVE_TAB_TTL_MS = 10 * 60_000;

export interface StoredFeedActiveTab {
  tab: FeedActiveTab;
  ts: number;
}

function isKnownFeedActiveTab(value: unknown): value is FeedActiveTab {
  return value === "for-you" || value === "following" || value === "live" || value === "discover";
}

/** Serialize the active tab + write timestamp for storage. */
export function serializeFeedActiveTab(tab: FeedActiveTab, ts: number): string {
  const payload: StoredFeedActiveTab = { tab, ts };
  return JSON.stringify(payload);
}

/**
 * Decide which tab (if any) to restore from a raw sessionStorage value: parse,
 * validate shape, check freshness, then re-validate the tab is still legal for
 * the CURRENT auth state ("following" requires login — the persisted tab may
 * be stale from a session where the user was logged in, or logged out since).
 * Returns `null` when nothing should be restored (missing/malformed/expired/
 * no-longer-legal) — the caller falls back to `FEED_DEFAULT_ACTIVE_TAB`. Pure —
 * `now`/`ttlMs` are injected so tests are deterministic and the domain never
 * reads the clock.
 */
export function decideRestoredFeedActiveTab(
  raw: string | null,
  now: number,
  ttlMs: number,
  isLoggedIn: boolean
): FeedActiveTab | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const p = parsed as Partial<StoredFeedActiveTab>;
  if (!isKnownFeedActiveTab(p.tab)) return null;
  const ts = typeof p.ts === "number" ? p.ts : 0;
  if (now - ts > ttlMs) return null;
  if (!isFeedTabVisible(p.tab, isLoggedIn)) return null;
  return coerceFeedActiveTab(p.tab, isLoggedIn);
}
